import { MouseEvent, useState } from 'react'

import MenuItemFlat from '@components/MenuItemFlat/MenuItemFlat'
import UserAvatar from '@components/UserAvatar/UserAvatar'
import { UserSummary } from '@domain'
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons'
import { faArrowRightFromBracket, faGear } from '@fortawesome/pro-solid-svg-icons'
import useLogout from '@hooks/useLogout'
import { Divider, IconButton, Menu, Stack, Typography } from '@mui/material'
import Help from '@views/Layout/PageHeader/Help/Help'
import { UserSettingsDrawer } from '@views/Layout/PageHeader/UserSettings/UserSettings'

type Props = {
	user: UserSummary
}

export default function UserMenu({ user }: Props) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
	const [settingsOpen, setSettingsOpen] = useState(false)
	const [helpOpen, setHelpOpen] = useState(false)
	const open = Boolean(anchorEl)

	const { mutate: logout } = useLogout()

	const handleOpen = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)
	const handleClose = () => setAnchorEl(null)
	const handleLogout = () => {
		logout()
	}

	return (
		<>
			<UserSettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
			<Help open={helpOpen} onClose={() => setHelpOpen(false)} />

			<IconButton
				onClick={handleOpen}
				aria-haspopup="true"
				aria-expanded={open ? 'true' : undefined}
				data-testid="header-user-avatar"
			>
				<UserAvatar user={user} size="small" />
			</IconButton>

			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				data-testid="header-user-menu"
			>
				<Stack sx={{ px: 2, py: 1 }}>
					<Typography variant="body1">
						<b>{user.displayName}</b>
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{user.email}
					</Typography>
				</Stack>

				<Divider sx={{ my: 1 }} />

				<MenuItemFlat
					onClick={() => {
						handleClose()
						setSettingsOpen(true)
					}}
					icon={faGear}
					text="Settings"
					data-testid="header-user-menu-settings"
				/>
				<MenuItemFlat
					onClick={() => {
						handleClose()
						setHelpOpen(true)
					}}
					icon={faCircleQuestion}
					text="Help"
					data-testid="header-user-menu-help"
				/>

				<Divider />

				<MenuItemFlat
					onClick={() => {
						handleClose()
						handleLogout()
					}}
					icon={faArrowRightFromBracket}
					text="Log out"
					data-testid="header-user-menu-logout"
				/>
			</Menu>
		</>
	)
}
